import {
  Button,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerHeaderButton,
  DrawerTitle
} from 'design-system';
import { Bot, History, Plus, X } from 'lucide-react';

import Chat from '@/features/ai-chat/components/AskGXDrawer/Chat/Chat';
import ChatHistory from '@/features/ai-chat/components/AskGXDrawer/ChatHistory/ChatHistory';
import InputDisclaimer from '@/features/ai-chat/components/AskGXDrawer/InputDisclaimer/InputDisclaimer';
import MessageInput from '@/features/ai-chat/components/AskGXDrawer/MessageInput/MessageInput';
import { ChatProvider } from './ChatProvider';

import { useAskGXContent } from './useAskGXContent';

const AskGXContent = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const {
    conversationId,
    setConversationId,
    messages,
    anchorEl,
    setAnchorEl,
    historyBtnRef,
    handleHistoryClick
  } = useAskGXContent();

  return (
    <Drawer
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) onClose();
      }}
    >
      <DrawerContent className='max-w-[min(53rem,100vw)]'>
        <DrawerHeader className='min-h-20 gap-0 border-gx-border sm:min-h-22'>
          <div className='flex min-w-0 flex-1 items-center justify-between gap-2 px-3 sm:gap-6 sm:px-6 sm:pr-7'>
            <div className='flex min-w-0 items-center gap-2 sm:gap-3'>
              <div className='flex size-9 shrink-0 items-center justify-center rounded-lg border border-gx-highlight/45 bg-gx-highlight/10 text-gx-highlight sm:size-10'>
                <Bot
                  aria-hidden='true'
                  className='size-5'
                />
              </div>
              <div className='min-w-0'>
                <DrawerTitle className='h-auto gap-0 truncate text-sm font-semibold whitespace-nowrap sm:text-base [&_svg]:size-5'>
                  GX Go Assistant
                </DrawerTitle>
                <DrawerDescription className='mt-1 hidden items-center gap-2 text-xs font-normal text-gx-subtext sm:flex'>
                  <span
                    aria-hidden='true'
                    className={`size-1.5 rounded-full ${
                      conversationId ? 'bg-gx-success' : 'bg-gx-text-disabled'
                    }`}
                  />
                  {conversationId ? 'Ready to help' : 'Starting a secure session'}
                </DrawerDescription>
              </div>
            </div>
            <Button
              aria-label='New chat'
              className='size-8 shrink-0 border-gx-border bg-transparent px-0 text-gx-text hover:border-gx-highlight/60 hover:bg-gx-highlight/10 hover:text-gx-highlight sm:w-auto sm:px-3'
              disabled={messages.length === 0 && conversationId !== null}
              size='default'
              variant='outline'
              onClick={() => {
                setConversationId('');
              }}
            >
              <Plus
                aria-hidden='true'
                data-icon='inline-start'
              />
              <span className='hidden sm:inline'>New chat</span>
            </Button>
          </div>
          <div className='flex'>
            <DrawerHeaderButton
              ref={historyBtnRef}
              active={Boolean(anchorEl)}
              className='w-10 border-l border-gx-border bg-transparent hover:bg-gx-tile-dark sm:w-14'
              label='Conversation history'
              onClick={handleHistoryClick}
            >
              <History />
            </DrawerHeaderButton>
            <DrawerHeaderButton
              className='w-10 border-l border-gx-border bg-transparent hover:bg-gx-tile-dark sm:w-14'
              label='Close'
              onClick={onClose}
            >
              <X />
            </DrawerHeaderButton>
          </div>
        </DrawerHeader>
        <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
          {open && (
            <>
              <Chat />
              <MessageInput maxRows={3} />
              <InputDisclaimer />
              <ChatHistory
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
              />
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

const AskGXDrawer = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  return (
    <ChatProvider open={open}>
      <AskGXContent
        open={open}
        onClose={onClose}
      />
    </ChatProvider>
  );
};

export default AskGXDrawer;
